import * as THREE from 'three';
import React, { useEffect } from 'react';
import { isAndroid, isMobile } from 'react-device-detect';
import HotspotConstructor from '../Hotspot/HotspotConstructor';
import { getIntersectedMarkerObject } from '@3jss/Scene/interactionHelpers';

const DESKTOP_THRESHOLD = 0.005;
const MOBILE_THRESHOLD = 0.1;
const MIN_ZOOM_FOV = 20;
const MAX_ZOOM_FOV = 70;

const raycaster = new THREE.Raycaster();

/**
 *
 * @module MouseListener
 *
 * @description
 * Mouse listener for scene. Handles mouse events and calls callbacks `onMouseDown`, `onMouseUp`, `onMouseDown` and `onMouseMove`.
 *
 * @param sceneRef - The ref of the scene.
 * @param onMouseDown - Callback for mouse down event.
 * @param onMouseUp - Callback for mouse up event.
 * @param onMouseMove - Callback for mouse move event.
 * @param rendererRef - The ref of the renderer.
 * @param allowEventsForMarkerTypeOnly - If true, only marker type events will be allowed.
 * @param cameraRef - The ref of the camera.
 * @param sceneId - The id of the scene.
 * @param orbitControlsConfig - The config for orbit controls.
 * @returns {null}
 *
 * @example
 * <MouseListener
 *   sceneRef={sceneRef}
 *   onMouseDown={onMouseDown}
 *   onMouseUp={onMouseUp}
 *   onMouseMove={onMouseMove}
 *   rendererRef={rendererRef}
 *   allowEventsForMarkerTypeOnly={allowEventsForMarkerTypeOnly}
 *   cameraRef={cameraRef}
 *   sceneId={sceneId}
 *   orbitControlsConfig={orbitControlsConfig}
 *   />
 *
 * @description
 *   This component can be used for adding mouse event listeners to the scene.
 */
const MouseListener = ({
	sceneRef,
	onMouseDown = () => {},
	onMouseUp = () => {},
	onMouseMove = () => {},
	rendererRef,
	allowEventsForMarkerTypeOnly,
	cameraRef,
	sceneId,
	orbitControlsConfig,
	isScrollDisabled
}) => {
	//Mouse positions
	const mouseCoord = new THREE.Vector2();
	const mouseStart = new THREE.Vector2();

	const eventMouseStart = new THREE.Vector2();
	const eventMouseEnd = new THREE.Vector2();

	let hoveredMarker = null;

	const _preventContextMenu = (e) => {
		/** Prevent Context Menu on right click **/
		if (e.target.id === 'modal-overlay') {
			e.preventDefault();
			return false;
		}
		return true;
	};

	const _getMousePosition = (e, isMobileEvent) => {
		const rect = rendererRef.current.domElement.getBoundingClientRect();
		const { top, left, width, height } = rect;
		let x;
		let y;
		let clientX = e.clientX;
		let clientY = e.clientY;

		if (isMobileEvent) {
			const eventData =
				e.type === 'touchend' ? e.changedTouches : e.touches;
			//Do not use pageX/pageY here!!!!
			clientX = eventData[0].clientX;
			clientY = eventData[0].clientY;
		}

		x = ((clientX - left) / width) * 2 - 1;
		y = -((clientY - top) / height) * 2 + 1;

		if (!x || !y) console.error('Wrong mouse coordinate computation');

		return { x, y };
	};

	const resetHovers = () => {
		hoveredMarker?.onUnhover && hoveredMarker.onUnhover();
		hoveredMarker = null;
	};

	const _onMouseMove = (e) => {
		const isMobileEvent = e.type === 'touchmove';

		const coords = _getMousePosition(e, isMobileEvent);

		mouseStart.set(coords.x, coords.y);
		mouseCoord.set(coords.x, coords.y);

		raycaster.setFromCamera(mouseStart, cameraRef.current);

		const intersects = raycaster.intersectObjects(
			sceneRef.current.children,
		);

		const sceneObject = getIntersectedMarkerObject(intersects);

		const marker = sceneObject?.object;

		//three cases,
		// 1- new hover, 2- already hovered, 3- no hovers

		// Hovered and marker found
		if (marker) {
			// Marker not same as existing hoveredMarker
			if (hoveredMarker && hoveredMarker !== marker) {
				// unHover current hoveredMarker
				hoveredMarker.onUnhover && hoveredMarker.onUnhover();
			}

			// Mark new hoveredMarker
			hoveredMarker = marker;
			marker.onHover && marker.onHover();
		}

		// No hoveredMarker markers
		if (!marker && hoveredMarker) {
			resetHovers();
		}

		//call public callback
		if (onMouseMove) onMouseMove({ e, marker, sceneObject });
	};

	const mouseWheelHandler = (e) => {
		if (
			orbitControlsConfig?.zoom &&
			orbitControlsConfig.zoom?.desktop == false
		)
			return;
		if (isScrollDisabled) return;
		const fovDelta = e.deltaY;
		const temp = cameraRef.current.fov + fovDelta * 0.05;

		if (temp > MIN_ZOOM_FOV && temp < MAX_ZOOM_FOV) {
			cameraRef.current.fov = temp; // eslint-disable-line
			cameraRef.current.updateProjectionMatrix();
		}
	};

	const _onMouseDown = (e) => {
		const isMobileEvent = e.type === 'touchstart';
		const coord = _getMousePosition(e, isMobileEvent);
		eventMouseStart.set(coord.x, coord.y);
		mouseCoord.set(coord.x, coord.y); //mouseCoord should keep initial click position

		//Public interface
		if (onMouseDown) onMouseDown(e, coord);
	};

	const _onMouseUp = (e) => {
		const isMobileEvent = e.type === 'touchend';

		if (isMobileEvent) e.preventDefault();
		const coords = _getMousePosition(e, isMobileEvent);
		eventMouseEnd.set(coords.x, coords.y);

		const dragDistance = eventMouseStart.distanceTo(eventMouseEnd);

		const isDragEvent = isAndroid
			? dragDistance > MOBILE_THRESHOLD
			: dragDistance > DESKTOP_THRESHOLD;

		if (isDragEvent) {
			return;
		}

		raycaster.setFromCamera(mouseCoord, cameraRef.current);

		const intersects = raycaster.intersectObjects(
			sceneRef.current.children,
			true,
		);

		const sceneObject = getIntersectedMarkerObject(intersects);

		if (isAndroid && sceneObject?.object?.userData?.owner?.onClick) {
			sceneObject?.object?.userData?.owner?.onClick();
		} else if (sceneObject?.object?.onClick) {
			sceneObject.object?.onClick();
		} else if (sceneObject?.object?.onClick) {
			sceneObject?.object?.onClick();
		}

		const marker =
			sceneObject?.object?.owner || sceneObject?.object?.userData?.owner;

		// public method/callback
		if (onMouseUp) return onMouseUp(e, sceneObject, marker, isDragEvent);
	};

	const addThreeEditorMouseEventListeners = () => {
		rendererRef.current.domElement.addEventListener(
			'mousemove',
			_onMouseMove,
			{
				passive: true,
			},
		);
		rendererRef.current.domElement.addEventListener(
			'wheel',
			mouseWheelHandler,
			{
				passive: true,
			},
		);
		rendererRef.current.domElement.addEventListener(
			'mousedown',
			_onMouseDown,
			{
				passive: true,
			},
		);
		rendererRef.current.domElement.addEventListener('mouseup', _onMouseUp, {
			passive: true,
		});
		rendererRef.current.domElement.addEventListener(
			'contextmenu',
			_preventContextMenu,
			{
				passive: true,
			},
		);

		if (isMobile) {
			//Mobile
			rendererRef.current.domElement.addEventListener(
				'touchstart',
				_onMouseDown,
				{
					passive: true,
				},
			);
			rendererRef.current.domElement.addEventListener(
				'touchmove',
				_onMouseMove,
				{
					passive: true,
				},
			);
			rendererRef.current.domElement.addEventListener(
				'touchend',
				_onMouseUp,
				{
					passive: false,
				},
			);
		}
	};

	const removeThreeEditorMouseEventListeners = () => {
		rendererRef.current.domElement.removeEventListener(
			'mousemove',
			_onMouseMove,
			{
				passive: true,
			},
		);
		rendererRef.current.domElement.removeEventListener(
			'wheel',
			mouseWheelHandler,
			{
				passive: true,
			},
		);
		rendererRef.current.domElement.removeEventListener(
			'mousedown',
			_onMouseDown,
			{
				passive: true,
			},
		);
		rendererRef.current.domElement.removeEventListener(
			'mouseup',
			_onMouseUp,
			{
				passive: true,
			},
		);
		rendererRef.current.domElement.removeEventListener(
			'contextmenu',
			_preventContextMenu,
			{
				passive: true,
			},
		);

		if (isMobile) {
			//Mobile
			rendererRef.current.domElement.removeEventListener(
				'touchstart',
				_onMouseDown,
				{
					passive: true,
				},
			);
			rendererRef.current.domElement.removeEventListener(
				'touchmove',
				_onMouseMove,
				{
					passive: true,
				},
			);
			rendererRef.current.domElement.removeEventListener(
				'touchend',
				_onMouseUp,
				{
					passive: false,
				},
			);
		}
	};

	useEffect(() => {
		addThreeEditorMouseEventListeners();
		return () => {
			removeThreeEditorMouseEventListeners();
			resetHovers();
		};
	}, []);

	useEffect(() => {
		resetHovers();
	}, [sceneId]);

	return null;
};

export default MouseListener;
